import { Component, Listen, Element, Prop } from '@stencil/core';
import { debounce } from 'debounce';
import { responsive } from '../../global/utility/responsive/responsive';
import { versionComponent } from '../../global/inject-dependencies';

@Component({
  tag: 'ui-quickfact-group',
  styleUrl: 'ui-quickfact-group.scss',
  shadow: false,
})
export class UiQuickfactGroup {
  @Element() el: HTMLElement;

  @Prop({ mutable: true }) quickfacts: any;

  @Prop() iconSize: 'small' | 'large' = 'large';

  @Prop() iconLayout: 'above' | 'left' = 'above';

  @Prop() componentWidth: number;

  resizeObserver: ResizeObserver;

  componentWillLoad() {
    // FIXME: lazy loading disabled due to sizing issues --> see readme.md
    const images = [].slice.call(this.el.querySelectorAll('ui-image'));
    images.forEach((element) => {
      element.setAttribute('lazy-loading', 'auto');
    });

    this.quickfacts = [].slice.call(this.el.querySelectorAll('ui-quickfact'));
  }

  componentDidLoad() {
    this.quickfacts.forEach((quickfact) => {
      const updatedQuickfact = quickfact;
      updatedQuickfact.iconSize = this.iconSize;
      updatedQuickfact.iconLayout = this.iconLayout;
    });

    this.resizeObserver = new ResizeObserver(() => {
      if (this.iconLayout === 'above') {
        const equalizeHeightsDebounced = debounce(
          this.equalizeHeights.bind(this),
          100,
          false
        );
        equalizeHeightsDebounced();
      }
    });
    this.resizeObserver.observe(this.el);

    responsive(this.el);
  }

  disconnectedCallback() {
    this.resizeObserver.disconnect();
  }

  /**
   * get amount of columns
   */
  get numberOfColumns(): number {
    return parseInt(
      window
        .getComputedStyle(this.el)
        .getPropertyValue('--quickfact-number-columns'),
      10
    );
  }

  /**
   * Get an array with the heights of all quickfact headlines ordered in arrays per row.
   */
  getHeadlineHeights(rowLength: number): number[][] {
    return this.quickfacts
      .map((e) => e.shadowRoot.querySelector('.headline').offsetHeight)
      .reduce((aggr, current, i) => {
        aggr[(i - (i % rowLength)) / rowLength] = [
          ...(aggr[(i - (i % rowLength)) / rowLength] || []),
          current,
        ];
        return aggr;
      }, []);
  }

  /**
   * get the largest headline height in a specific row
   */
  getMaxHeadlineHeightForRow(rowIndex: number) {
    return Math.max(...this.getHeadlineHeights(this.numberOfColumns)[rowIndex]);
  }

  /**
   * Get the row in which a quickfact component is located. First row returns 0, the second 1 etc.
   */
  getRowForQuickfact(quickfact): number {
    return (
      (this.quickfacts.indexOf(quickfact) -
        (this.quickfacts.indexOf(quickfact) % this.numberOfColumns)) /
      this.numberOfColumns
    );
  }

  /**
   * Get the headline height for a quickfact component based on the highest headline in the same row
   */
  getHeightForQuickfactHeadline(quickfact): number {
    return this.getMaxHeadlineHeightForRow(this.getRowForQuickfact(quickfact));
  }

  /**
   * Set the largest headline height per line to the respective Quickfact headlines
   */
  equalizeHeights() {
    this.quickfacts.forEach((quickfact) => {
      quickfact.style.setProperty('--headline-height', 'auto');
      quickfact.style.setProperty(
        '--headline-height',
        `${this.getHeightForQuickfactHeadline(quickfact)}px`
      );
    });
  }

  @Listen('quickfactClicked')
  manageQuickfacts(e) {
    /* eslint no-param-reassign: ["error", { "props": false }] */
    const targetQuickfact: HTMLUiQuickfactElement = e.detail.quickfact;

    this.quickfacts.forEach((quickfact) => {
      if (quickfact !== targetQuickfact) {
        quickfact.open = false;
      }
    });
  }
}

versionComponent(UiQuickfactGroup);
